import React, { useCallback, useEffect } from 'react';

interface QPlayerIframeProps {
	mediaId?: string;
	url?: string;
	pause?: (_fn: () => void) => void;
	play?: (_fn: () => void) => void;
	mute?: (_fn: () => void) => void;
	unMute?: (_fn: () => void) => void;
	movePlayerToPosition?: (_fn: (_pos: number, _message?: string) => void) => void;
	togglePlay?: (_isPlaying: boolean) => void;
	onPositionChange?: (_pos: number) => void;
}

const PLAYER_IDENTIFIER = 'Qplayer.Embedded';
const PLAYER_ID = 'speech-to-text-qplayer-iframe';

const QPlayerIframe = ({
	mediaId, url, pause, play, movePlayerToPosition, mute, unMute, togglePlay, onPositionChange
}: QPlayerIframeProps) => {
	const handlePause = useCallback(() => {
		const playerIframe = document.getElementById(PLAYER_ID) as HTMLIFrameElement;
		if (playerIframe && playerIframe.contentWindow) {
			playerIframe.contentWindow.postMessage({ identifier: PLAYER_IDENTIFIER, command: 'pause' }, 'https://play2.qbrick.com/');
		}
	}, []);

	const handlePlay = useCallback(() => {
		const playerIframe = document.getElementById(PLAYER_ID) as HTMLIFrameElement;
		if (playerIframe && playerIframe.contentWindow) {
			playerIframe.contentWindow.postMessage({ identifier: PLAYER_IDENTIFIER, command: 'play' }, 'https://play2.qbrick.com/');
		}
	}, []);

	const handleMute = useCallback(() => {
		const playerIframe = document.getElementById(PLAYER_ID) as HTMLIFrameElement;
		if (playerIframe && playerIframe.contentWindow) {
			playerIframe.contentWindow.postMessage({ identifier: PLAYER_IDENTIFIER, command: 'volume', metadata: { volume: 0 } }, 'https://play2.qbrick.com/');
		}
	}, []);

	const handleUnMute = useCallback(() => {
		const playerIframe = document.getElementById(PLAYER_ID) as HTMLIFrameElement;
		if (playerIframe && playerIframe.contentWindow) {
			playerIframe.contentWindow.postMessage({ identifier: PLAYER_IDENTIFIER, command: 'volume', metadata: { volume: 0.5 } }, 'https://play2.qbrick.com/');
		}
	}, []);

	const handleMovePlayerToPosition = useCallback((pos: number, message = '') => {
		const playerIframe = document.getElementById(PLAYER_ID) as HTMLIFrameElement;
		if (playerIframe && playerIframe.contentWindow) {
			playerIframe.contentWindow.postMessage(
				{ identifier: PLAYER_IDENTIFIER, command: 'position', metadata: { position: pos }, message: message },
				'https://play2.qbrick.com/'
			);
		}
	}, []);

	const handleOnMessageFromIframe = useCallback((event: MessageEvent) => {
		if (event.data.identifier !== "Qplayer" || event.data.mediaId !== mediaId) {
			return;
		}

		if (event.data.type === "play") {
			togglePlay?.(true);
		}
		if (event.data.type === "pause") {
			togglePlay?.(false);
		}
		if (event.data.type === "position") {
			onPositionChange?.(event.data.metadata.position);
		}
	}, [mediaId, togglePlay, onPositionChange]);

	useEffect(() => {
		movePlayerToPosition?.(handleMovePlayerToPosition);
		pause?.(handlePause);
		play?.(handlePlay);
		mute?.(handleMute);
		unMute?.(handleUnMute);

		window.addEventListener('message', handleOnMessageFromIframe);
		return () => {
			window.removeEventListener('message', handleOnMessageFromIframe);
		};
	}, [handleMovePlayerToPosition, handleOnMessageFromIframe, handlePause, handlePlay, handleMute, handleUnMute, movePlayerToPosition, pause, play, mute, unMute]);

	return (
		<iframe
			id={PLAYER_ID}
			src={url}
			frameBorder="0"
			height="100%"
			width="100%"
			style={{ position: 'absolute', top: '0', left: '0' }}
			allow="autoplay"
			title="QPlayer Iframe"
		/>
	);
};

export default QPlayerIframe;
